/**
 * DataGrid search support.
 * Both the .NET row filter and the highlight work on the SAME content —
 * the text actually rendered in each row's data cells (action buttons,
 * links and form controls excluded) — so filtering and highlighting can
 * never disagree. Highlighting uses the CSS Custom Highlight API, so the
 * DOM is never mutated.
 */

'use strict';

/** Registry key under which the grid's highlight is stored in CSS.highlights. */
const HIGHLIGHT_KEY = 'xa-grid-search';

/** Data cells only — action cells (xa-grid__cell-actions) are separate tds. */
const CELL_SELECTOR = 'td.xa-grid__cell';

/** Elements whose labels never count as searchable content. */
const EXCLUDED_TAGS = new Set(['BUTTON', 'A', 'INPUT', 'SELECT', 'TEXTAREA']);

/** CSS.highlights registry, or undefined when the API is unsupported. */
function highlightRegistry(): Map<string, Highlight> | undefined {
    return (CSS as unknown as { highlights?: Map<string, Highlight> }).highlights;
}

/**
 * Search folding: case-insensitive and digit-form-insensitive (Persian ۰-۹
 * and Arabic ٠-٩ map to ASCII). One UTF-16 unit in, one out — offsets into
 * folded strings stay valid for the original text, which the highlight
 * relies on.
 */
function fold(text: string): string {
    let out = '';
    for (let i = 0; i < text.length; i++) {
        const code = text.charCodeAt(i);
        if (code >= 0x06f0 && code <= 0x06f9) {
            out += String.fromCharCode(code - 0x06f0 + 48);
        } else if (code >= 0x0660 && code <= 0x0669) {
            out += String.fromCharCode(code - 0x0660 + 48);
        } else {
            const lower = text[i].toLowerCase();
            // Guard against locale expansions (e.g. 'İ'.toLowerCase()) that
            // would break the 1:1 offset mapping.
            out += lower.length === 1 ? lower : text[i];
        }
    }
    return out;
}

/** Text segment of one text node, with offsets into the accumulated string. */
interface Segment {
    node: Text;
    start: number;
    end: number;
}

/**
 * Recursively collects the text of a cell, returning it as one string while
 * recording the (node, start, end) offsets of every text node. Subtrees of
 * excluded elements (buttons, links, inputs) are skipped.
 */
function collectCellText(cell: Node, segments: Segment[]): string {
    let text = '';
    for (const child of cell.childNodes) {
        if (child.nodeType === Node.TEXT_NODE) {
            const node = child as Text;
            const value = node.data ?? '';
            segments.push({ node, start: text.length, end: text.length + value.length });
            text += value;
        } else if (child.nodeType === Node.ELEMENT_NODE && !EXCLUDED_TAGS.has(child.nodeName)) {
            text += collectCellText(child, segments);
        }
    }
    return text;
}

/**
 * Highlights occurrences of `searchText` inside the data cells of `gridElement`.
 * Matching is case- and digit-form-insensitive; empty or null text clears the highlight.
 */
export function highlight(gridElement: HTMLElement | null, searchText: string | null): void {
    const registry = highlightRegistry();

    // Remove the previous highlight first — a single registry entry keeps
    // this O(changes) instead of O(accumulated matches).
    registry?.delete(HIGHLIGHT_KEY);

    const needle = searchText?.trim() ? fold(searchText.trim()) : '';
    if (!gridElement || !needle || !registry) {
        return;
    }

    const result = new Highlight();
    gridElement.querySelectorAll(CELL_SELECTOR).forEach((cell) => {
        const segments: Segment[] = [];
        const haystack = fold(collectCellText(cell, segments));
        if (!haystack) {
            return;
        }

        let from = 0;
        while (from <= haystack.length - needle.length) {
            const at = haystack.indexOf(needle, from);
            if (at < 0) {
                break;
            }

            // Map the folded offset back onto its containing text node —
            // valid because fold preserves UTF-16 offsets.
            for (const segment of segments) {
                if (segment.start <= at && at < segment.end) {
                    const range = new Range();
                    range.setStart(segment.node, at - segment.start);
                    range.setEnd(segment.node, Math.min(at - segment.start + needle.length, segment.end - segment.start));
                    result.add(range);
                    break;
                }
            }
            from = at + needle.length;
        }
    });

    if (result.size > 0) {
        registry.set(HIGHLIGHT_KEY, result);
    }
}

/**
 * Collects the rendered searchable text of every data row, in DOM order —
 * one string per row, cells joined with newlines. Action cells and the text
 * of buttons/links/inputs are excluded, matching what the highlight covers.
 * The .NET filter runs on exactly these strings.
 */
export function collectRowTexts(gridElement: HTMLElement | null): string[] {
    if (!gridElement) {
        return [];
    }

    const rows: string[] = [];
    gridElement.querySelectorAll('tbody tr').forEach((row) => {
        const cells = row.querySelectorAll(CELL_SELECTOR);
        if (cells.length === 0) {
            // New-row editor rows carry no data cells — not a data row.
            return;
        }

        const segments: Segment[] = [];
        const texts: string[] = [];
        cells.forEach((cell) => texts.push(collectCellText(cell, segments)));
        rows.push(texts.join('\n'));
    });
    return rows;
}
